import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export function generateInvoicePdf(booking, businessInfo) {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4"
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Colors
  const maroon = [131, 24, 67]; // #831843
  const darkGray = [40, 40, 40];
  const lightGray = [100, 100, 100];
  const borderGray = [220, 220, 220];

  // 1. Header Banner / Top Strip
  doc.setFillColor(...maroon);
  doc.rect(0, 0, pageWidth, 28, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("BookMyEvents", 14, 12);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("EVENT BOOKING & TAX INVOICE", 14, 18);

  const bookingIdShort = (booking.id || "").slice(0, 8).toUpperCase();
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(`INVOICE #: BME-${bookingIdShort}`, pageWidth - 14, 12, { align: "right" });
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(`Date: ${new Date().toLocaleDateString("en-IN")}`, pageWidth - 14, 18, { align: "right" });

  let curY = 36;

  // 2. Business (Hall) Details & Customer Details Grid
  doc.setDrawColor(...borderGray);
  doc.setLineWidth(0.3);
  doc.roundedRect(14, curY, (pageWidth - 32) / 2, 38, 2, 2, "D");
  doc.roundedRect(14 + (pageWidth - 32) / 2 + 4, curY, (pageWidth - 32) / 2, 38, 2, 2, "D");

  const boxWidth = (pageWidth - 32) / 2;

  // Left Box: Hall / Venue Details
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...maroon);
  doc.text("VENUE / HALL DETAILS", 18, curY + 6);

  const hallName = businessInfo?.business_name || "Grand Royal Palace & Convention Hall";
  const hallPhone = businessInfo?.phone || "+91 98450 12345";
  const hallEmail = businessInfo?.email || "bookings@grandroyalpalace.com";
  const hallAddress = businessInfo?.address || "45 Heritage Boulevard, Bengaluru 560068";

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...darkGray);
  const hallLines = doc.splitTextToSize(hallName, boxWidth - 8);
  doc.text(hallLines.slice(0, 1), 18, curY + 12);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...lightGray);
  doc.text(`Phone: ${hallPhone}`, 18, curY + 17);
  doc.text(`Email: ${hallEmail}`, 18, curY + 22);
  const addrLines = doc.splitTextToSize(`Address: ${hallAddress}`, boxWidth - 8);
  doc.text(addrLines.slice(0, 2), 18, curY + 27);

  // Right Box: Customer & Event Info
  const rightX = 14 + boxWidth + 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...maroon);
  doc.text("CUSTOMER & EVENT DETAILS", rightX, curY + 6);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...darkGray);
  const clientLines = doc.splitTextToSize(booking.client_name || "Client Name", boxWidth - 8);
  doc.text(clientLines.slice(0, 1), rightX, curY + 12);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...lightGray);
  doc.text(`Phone: ${booking.client_phone || "—"}`, rightX, curY + 17);
  doc.text(`Event: ${booking.event_type || "Wedding"}`, rightX, curY + 22);
  const dateStr = booking.start_date === booking.end_date
    ? booking.start_date
    : `${booking.start_date} to ${booking.end_date}`;
  doc.text(`Dates: ${dateStr}`, rightX, curY + 27);
  doc.text(`Hours: ${booking.start_time} - ${booking.end_time} (${booking.slot_type?.toUpperCase()} SLOT)`, rightX, curY + 32);

  curY += 44;

  // 3. Itemized Charges Table
  const hallRent = booking.hall_rent ?? booking.hallRent ?? booking.rent_amount ?? (booking.totalAmount || booking.total_amount || 0);
  const labor = Number(booking.labor_charges ?? booking.laborCharges ?? 0);
  const electricity = Number(booking.electricity_charges ?? booking.electricityCharges ?? 0);
  const maintenance = Number(booking.maintenance_fee ?? booking.maintenanceFee ?? 0);
  const applyGst = Boolean(booking.apply_gst || booking.applyGst || booking.sgst_amount > 0 || booking.cgst_amount > 0);
  const sgst = Number(booking.sgst_amount ?? booking.sgstAmount ?? (applyGst ? hallRent * 0.09 : 0));
  const cgst = Number(booking.cgst_amount ?? booking.cgstAmount ?? (applyGst ? hallRent * 0.09 : 0));
  const additionalCharges = booking.additional_charges || [];

  const tableRows = [];

  // 1. Hall Rent
  tableRows.push(["1", "Convention Hall Rent (Base Venue)", "1 Slot / Duration", `INR ${Number(hallRent).toLocaleString("en-IN")}`]);

  // 2. Labor Charges
  if (labor > 0) {
    tableRows.push([String(tableRows.length + 1), "Labor Charges (Cleaning & Setup Staff)", "Fixed Fee", `INR ${Number(labor).toLocaleString("en-IN")}`]);
  }

  // 3. Electricity Charges
  if (electricity > 0) {
    tableRows.push([String(tableRows.length + 1), "Electricity & Utility Charges", "Standard Usage", `INR ${Number(electricity).toLocaleString("en-IN")}`]);
  }

  // 4. Maintenance Fee
  if (maintenance > 0) {
    tableRows.push([String(tableRows.length + 1), "Venue Maintenance & Upkeep Fee", "Standard Fee", `INR ${Number(maintenance).toLocaleString("en-IN")}`]);
  }

  // 5. Additional Charges
  additionalCharges.forEach((ac) => {
    if (ac.name && parseFloat(ac.amount) > 0) {
      tableRows.push([
        String(tableRows.length + 1),
        `Additional Service: ${ac.name}`,
        "Optional Add-on",
        `INR ${Number(ac.amount).toLocaleString("en-IN")}`
      ]);
    }
  });

  // 6. GST Lines if applied
  if (applyGst) {
    tableRows.push([String(tableRows.length + 1), "SGST (9% on Hall Rent)", "State GST (9%)", `INR ${Number(sgst).toLocaleString("en-IN")}`]);
    tableRows.push([String(tableRows.length + 1), "CGST (9% on Hall Rent)", "Central GST (9%)", `INR ${Number(cgst).toLocaleString("en-IN")}`]);
  }

  autoTable(doc, {
    startY: curY,
    head: [["#", "Charge Description", "Type / Notes", "Amount (INR)"]],
    body: tableRows,
    theme: "striped",
    headStyles: {
      fillColor: maroon,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8.5
    },
    bodyStyles: {
      fontSize: 8,
      textColor: darkGray
    },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: 85 },
      2: { cellWidth: 45 },
      3: { cellWidth: 42, halign: "right", fontStyle: "bold" }
    },
    margin: { left: 14, right: 14 }
  });

  curY = doc.lastAutoTable.finalY + 6;

  // 4. Financial Summary Box (Right aligned)
  const summaryBoxWidth = 85;
  const summaryBoxX = pageWidth - 14 - summaryBoxWidth;

  const totalAmount = booking.total_amount || 0;
  const advancePaid = booking.advance_paid || 0;
  const balanceDue = booking.balance_due !== undefined ? booking.balance_due : Math.max(0, totalAmount - advancePaid);

  doc.setFillColor(255, 241, 242); // #fff1f2
  doc.setDrawColor(...maroon);
  doc.setLineWidth(0.3);
  doc.roundedRect(summaryBoxX, curY, summaryBoxWidth, 34, 2, 2, "FD");

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...darkGray);
  doc.text("Total Amount:", summaryBoxX + 6, curY + 8);
  doc.setFont("helvetica", "bold");
  doc.text(`INR ${Number(totalAmount).toLocaleString("en-IN")}`, summaryBoxX + summaryBoxWidth - 6, curY + 8, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setTextColor(5, 150, 105); // emerald
  doc.text("Advance Paid:", summaryBoxX + 6, curY + 16);
  doc.setFont("helvetica", "bold");
  doc.text(`INR ${Number(advancePaid).toLocaleString("en-IN")}`, summaryBoxX + summaryBoxWidth - 6, curY + 16, { align: "right" });

  doc.setDrawColor(...borderGray);
  doc.line(summaryBoxX + 4, curY + 20, summaryBoxX + summaryBoxWidth - 4, curY + 20);

  doc.setFontSize(10);
  doc.setTextColor(...maroon);
  doc.text("Balance Due:", summaryBoxX + 6, curY + 28);
  doc.text(`INR ${Number(balanceDue).toLocaleString("en-IN")}`, summaryBoxX + summaryBoxWidth - 6, curY + 28, { align: "right" });

  // Left notes & terms
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...darkGray);
  doc.text("Terms & Payment Policy:", 14, curY + 6);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...lightGray);
  doc.text("1. Advance payment is non-refundable upon cancellation within 30 days.", 14, curY + 12);
  doc.text("2. Remaining balance must be settled prior to event check-in.", 14, curY + 17);
  doc.text("3. Generated automatically via BookMyEvents Web & Mobile Portal.", 14, curY + 22);

  // 5. Footer Strip
  doc.setDrawColor(...borderGray);
  doc.line(14, pageHeight - 14, pageWidth - 14, pageHeight - 14);

  doc.setFontSize(7.5);
  doc.setTextColor(...lightGray);
  doc.text("Thank you for booking with us! For assistance, contact venue manager.", 14, pageHeight - 9);
  doc.text("Page 1 of 1", pageWidth - 14, pageHeight - 9, { align: "right" });

  const fileName = `Invoice_${(booking.client_name || "Booking").replace(/[^a-zA-Z0-9]/g, "_")}_${booking.start_date || "date"}.pdf`;
  doc.save(fileName);
}
